/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 03.06.17.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";
import Select from "../../../components/Bootstrap/Select";
import Pagination from "../../../components/Bootstrap/Pagination";
import TableHead from "./TableHead";
import TableBody from "./TableBody";
import t from "../../../trans";
import CONFIG from "../../../config";
import Schema from "../../../Schema";
import DataProvider from "../../../DataProvider";
import {deepCompare} from "../../../util";
import {SessionStore} from "../../../services/Session";

class DataTable extends React.Component {
    /**
     *
     */
    static propTypes = {
        /**
         * JSON Schema describing resource structure (main object of the schema should be of type "array"!)
         */
        schema: PropTypes.instanceOf(Schema).isRequired,
        /**
         * Data
         */
        data: PropTypes.arrayOf(PropTypes.object),
        /**
         * Actions
         */
        actions: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.node,
            action: PropTypes.string.isRequired
        })),
        location: PropTypes.object,
        pageSize: PropTypes.number,
        recordCount: PropTypes.number,
        estimated: PropTypes.bool,
        firstRecord: PropTypes.number,
        /**
         * Sort specification
         */
        sort: PropTypes.arrayOf(PropTypes.string),
        /**
         * Callback called when user click's on column header. Signature:
         * onSort(sortfield)
         */
        onSort: PropTypes.func,
        /**
         * Callback called when user selects new page size from dropdown. Signature:
         * onChangePageSize(newSize)
         */
        onChangePageSize: PropTypes.func,
        /**
         * Callback called when user click's on navigation buttons. Signature:
         * onGotoRecord(recordIdx)
         */
        onGotoRecord: PropTypes.func,
        /**
         * @type {OnActionCallback}
         */
        onAction: PropTypes.func,
        size: PropTypes.oneOf(["medium", "small"]),
        internalSort: PropTypes.bool,
        /**
         * If true then record number column will not be shown (first column)
         */
        hideRecColumn: PropTypes.bool,
        /**
         * Class names assigned to the table
         */
        className: PropTypes.string
    };

    static defaultProps = {
        size: "medium",
        recordCount: 0,
        estimated: false,
        internalSort: false,
        hideRecColumn: false,
        className: "table"
    };

    constructor(props) {
        super(props);
        this.state = {
            sort: props.sort,
            actions: SessionStore.filterAuthorizedActions((props.location ? props.location.pathname : null), props.actions)
        };
        this.onChangePageSize = this.onChangePageSize.bind(this);
        this.onSort = this.onSort.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            actions: SessionStore.filterAuthorizedActions((nextProps.location ? nextProps.location.pathname : null), nextProps.actions)
        });
    }

    getRowsInfo() {
        if (!this.props.data || !this.props.recordCount) return null;
        let recordCount = this.props.recordCount;
        let firstRecord = this.props.firstRecord + 1;
        let lastRecord = firstRecord + this.props.data.length - 1;

        if (this.props.estimated) return t("Showing %i to %i of %i estimated entries", firstRecord, lastRecord, recordCount);
        else return t("Showing %i to %i of %i entries", firstRecord, lastRecord, recordCount);
    }

    getPageSizeOptions() {
        return _.map(CONFIG.listPageSizes, function (value) {
            let label = null;
            switch (value) {
                case "":
                    label = t("default");
                    break;
                case "0":
                    label = t("all");
                    break;
                default:
                    label = value;
            }
            return {
                value: value,
                label: label
            }
        });
    }

    onChangePageSize(name, value) {
        if (this.props.onChangePageSize) this.props.onChangePageSize(value);
    }

    isClickAction() {
        if (!this.state.actions || this.state.actions.length == 0) return false;
        for (let action of this.state.actions) {
            if (!action.label && !action.icon) {
                return true;
            }
        }
        return false;
    }

    renderPageSizeSelect() {
        if (!this.props.pageSize) return null;
        return (
            <div className="form-group">
                {t("Show") + " "}
                <Select className="form-control input-sm" name="pageSize" options={this.getPageSizeOptions()} onChange={this.onChangePageSize} simple={true} value={this.props.pageSize}/>
                {" " + t("entries")}
            </div>
        );
    }

    getTableClass() {
        switch (this.props.size) {
            case "small":
                return "table-condensed";
            case "medium":
            default:
                return "";
        }
    }

    getPaginationClass() {
        switch (this.props.size) {
            case "small":
                return "pull-right pagination-sm";
            case "medium":
            default:
                return "pull-right";
        }
    }

    onSort(sort) {
        this.setState({sort: sort});
    }

    getDataProvider() {
        let data = this.props.data;
        if (this.props.internalSort) {
            // Sort internally based by sort spec:
            data = this.getSortedData(data, this.state.sort);
        }
        return new DataProvider(this.props.schema, data);
    }

    /**
     *
     * @param {Array} data
     * @param {Array} sort
     * @returns {Array}
     */
    getSortedData(data, sort) {
        if (!data || !sort || sort.length == 0 || !_.isArray(data) || data.length <= 1) return data;
        let sortInfo = [];
        for (let dir of sort) {
            if (!dir || dir.length == 0) continue;
            let field = dir;
            let descending = false;
            switch (dir.charAt(0)) {
                case "-":
                    descending = true;
                    field = dir.substring(1);
                    break;
                case "+":
                    descending = false;
                    field = dir.substring(1);
                    break;
                default:
                    break;
            }
            sortInfo.push([field, descending]);
        }
        if (sortInfo.length == 0) return data;

        data = _.clone(data);
        data.sort(function (a, b) {
            if (a == null) return (b == null ? 0 : -1);
            else if (b == null) return 1;
            for (let [field, desc] of sortInfo) {
                let result = deepCompare(this.getDataField(a, field), this.getDataField(b, field));
                if (result !== 0) return (desc ? -result : result);
            }
            return 0;
        }.bind(this));
        return data;
    }

    getDataField(data, field) {
        if (field == null) return null;
        field = field.split("/");
        for (let f of field) {
            data = data[f];
            if (!data) return null;
        }
        return data;
    }

    renderTableFooter() {
        if (!this.props.pageSize) return null;
        return (
            <div className="row form-inline">
                <div className="col-sm-4">
                    <div className="form-group">{this.getRowsInfo()}</div>
                </div>
                <div className="col-md-3">
                    {this.renderPageSizeSelect()}
                </div>
                <div className="col-md-5">
                    <Pagination className={this.getPaginationClass()}
                                pageSize={this.props.pageSize}
                                recordCount={this.props.recordCount}
                                firstRecord={this.props.firstRecord}
                                onGotoRecord={this.props.onGotoRecord} />
                </div>
            </div>
        );
    }

    render() {
        let dataProvider = this.getDataProvider();
        let isClickable = this.isClickAction();
        // Do we have additional actions (besides click action)
        let isActions = this.state.actions && this.state.actions.length > 0 && (!isClickable || this.state.actions.length > 1);
        let tableClass = this.getTableClass();
        return (
            <div className={this.props.className}>
                <table className={"table table-bordered table-hover" + (isClickable ? " clickable" : "") + (tableClass ? " " + tableClass : "")}>
                    <TableHead
                        dataProvider={dataProvider}
                        sort={this.props.internalSort ? this.state.sort : this.props.sort}
                        onSort={this.props.internalSort ? this.onSort : this.props.onSort}
                        appendEmptyColumns={isActions ? 1 : 0}
                        hideRecColumn={this.props.hideRecColumn}/>
                    <TableBody
                        dataProvider={dataProvider}
                        sort={this.props.internalSort ? this.state.sort : this.props.sort}
                        recordCount={this.props.recordCount}
                        firstRecord={this.props.firstRecord}
                        pageSize={this.props.pageSize}
                        hideRecColumn={this.props.hideRecColumn}
                        actions={this.state.actions}
                        onAction={this.props.onAction}
                        location={this.props.location}
                        />
                </table>
                {this.renderTableFooter()}
            </div>
        );
    }
}

export default DataTable;
